/* eslint-disable react/prop-types */ // TODO: add props
import { useMemo } from 'react';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import { Link } from 'react-router-dom';
import EPT from 'edrolo/propTypes';
import {
  ResultsTable,
  ChecklistReflectionSummary,
  RowExpansionArrow,
} from '../../../components';
import { IndicatorCell, INDICATOR_TYPES } from '../../../../../components';
import { StudentResponseBars } from '../StudentResponseBars';
import { getProfileQuestionResponseSummary } from '../../../utils';
import style from './TextQuestionReview.scss';

export const TextQuestionReview = ({
  question,
  solution,
  responses,
  highlightProfileId,
  getStudentCourseProgressUrl,
  classStudentProfiles,
  emInsights,
}) => {
  const profileQuestionResponseSummary = useMemo(
    () =>
      getProfileQuestionResponseSummary(
        classStudentProfiles,
        responses,
        solution,
        question
      ),
    [classStudentProfiles, responses, solution, question]
  );

  const selfMarkedQuestion = emInsights?.self_marked_questions?.find(
    (q) => q.question_id === question.get('id')
  );

  const questionMark = (+question.get('marks')).toFixed(0);

  const resultsTableColumns = [
    { id: 'leftSpacer', style: { width: '2%' } },

    {
      id: 'name',
      Header: 'Name',
      style: { width: '40%' },
      accessor: (row) => (
        <Link
          to={getStudentCourseProgressUrl(row.get('userProfileId'))}
        >{`${row.get('userFirstName')} ${row.get('userLastName')}`}</Link>
      ),
      sortType: (rowA, rowB) =>
        rowA.original.get('userLastName').toLowerCase() >
        rowB.original.get('userLastName').toLowerCase()
          ? 1
          : -1,
    },

    {
      id: 'studentResponse',
      Header: () => (
        <div className={style.studentResponseHeader}>Student Response</div>
      ),
      style: { width: '43%' },
      accessor: (row) => {
        return {
          responseText: row.get('answer'),
          checklistReflections: row.get('checklistReflections'),
        };
      },
      Cell: ({ cell, row }) => {
        const { responseText, checklistReflections } = cell.value;

        return row.isExpanded ? (
          <div className={style.responseWrapper}>
            <span className={style.fullWidthCell}>{responseText}</span>
            <ChecklistReflectionSummary reflections={checklistReflections} />
          </div>
        ) : responseText ? (
          <div className={style.responseWrapper}>
            <span>{responseText}</span>
          </div>
        ) : (
          <IndicatorCell
            value={'Not answered'}
            indicatorType={INDICATOR_TYPES.NEUTRAL}
          />
        );
      },
      disableSortBy: true,
    },

    {
      id: 'studentMark',
      Header: 'Student Mark',
      style: { width: '10%' },
      accessor: (row) => {
        const marksFinal = (+row.getIn(['response', 'marks_final'])).toFixed(0);
        if (marksFinal && !isNaN(marksFinal)) {
          return `${marksFinal}/${questionMark}`;
        }

        return 'N/A';
      },
      sortType: (rowA, rowB) =>
        rowA.original.getIn(['response', 'marks_final']) >
        rowB.original.getIn(['response', 'marks_final'])
          ? 1
          : -1,
    },

    {
      id: 'expander',
      Header: '',
      style: { width: '3%' },
      Cell: ({ row }) =>
        row.original.get('answer') ? (
          <div
            className={style.expanderWidgetContainer}
            {...row.getToggleRowExpandedProps()}
          >
            {row.isExpanded ? (
              <RowExpansionArrow expanded />
            ) : (
              <RowExpansionArrow />
            )}
          </div>
        ) : null,
    },

    { id: 'rightSpacer', style: { width: '2%' } },
  ];

  return (
    <>
      <div className={style.container}>
        <div>
          <h3 className={style.heading}>How students responded</h3>
          <StudentResponseBars
            profileQuestionResponseSummary={profileQuestionResponseSummary}
          />
        </div>
      </div>
      {selfMarkedQuestion && (
        <div className={style.content}>
          {`Average class mark:`}{' '}
          <span>
            {`${selfMarkedQuestion.average_marks.toFixed(1)}/${
              selfMarkedQuestion.total_marks_available
            }`}
          </span>
        </div>
      )}
      <div>
        <h3 className={style.heading}>Response details</h3>
        <ResultsTable
          data={profileQuestionResponseSummary}
          columns={resultsTableColumns}
          highlightProfileId={highlightProfileId}
        />
      </div>
    </>
  );
};

TextQuestionReview.propTypes = {
  classStudentProfiles: IPT.orderedMapOf(
    IPT.contains({
      user_id: PT.number.isRequired,
      profile_id: PT.number.isRequired,
      first_name: PT.string.isRequired,
      last_name: PT.string.isRequired,
      display_name: PT.string,
      is_active: PT.bool,
    })
  ),
  responses: IPT.listOf(EPT.ExamResponse),
  solution: IPT.contains({
    solution: IPT.contains({
      exemplar_response_checklist_items: IPT.listOf(
        IPT.contains({
          id: PT.number.isRequired,
          description: PT.string.isRequired,
        })
      ),
    }),
  }).isRequired,
  highlightProfileId: PT.number,
  getStudentCourseProgressUrl: PT.func,
  emInsights: PT.object,
};
